import { Controller } from "@hotwired/stimulus";
import * as Sentry from "@sentry/browser";

// Initializes Sentry frontend error monitoring.
// Attach to the <body> element in layouts so it connects once per page.
// Sentry.init() is only called once; Turbo Drive navigations re-trigger
// connect() but the guard prevents duplicate initialization.
//
// Usage:
//   %body{ data: { controller: "sentry" } }
//
// Reads configuration from <meta> tags:
//   - sentry-dsn: The Sentry DSN for the frontend project
//   - rails_env: The Rails environment (used as Sentry environment)
//   - user_id: The current user ID (set as Sentry user context)
//
let sentryInitialized = false;

export default class extends Controller {
  connect() {
    const dsn = this.#getMetaContent("sentry-dsn");
    if (!dsn) return;

    if (!sentryInitialized) {
      this.#initSentry(dsn);
      sentryInitialized = true;
    }

    // Update user context on every navigation (user may have changed)
    const userId = this.#getMetaContent("user_id");
    Sentry.setUser(userId ? { id: userId } : null);
  }

  #initSentry(dsn) {
    const environment = this.#getMetaContent("rails_env") || "development";

    Sentry.init({
      dsn,
      environment,

      // Only send 5% of transactions for performance monitoring
      tracesSampleRate: 0.05,

      // Capture 100% of error sessions for replay context
      replaysOnErrorSampleRate: 1.0,

      // Sample 0% of normal sessions for replay (opt-in if needed later)
      replaysSessionSampleRate: 0,

      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Mask all text, media, and form inputs to avoid capturing PII
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
        }),
      ],

      // Filter out noisy or irrelevant errors
      ignoreErrors: [
        // Browser extensions and third-party scripts
        "top.GLOBALS",
        "ResizeObserver loop",
        // Network errors that aren't actionable
        "Failed to fetch",
        "NetworkError",
        "Load failed",
      ],

      beforeSend(event) {
        // Skip errors originating from browser extensions
        const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
        if (frames.some((frame) => frame.filename?.includes("extension://"))) {
          return null;
        }

        // Strip user metadata down to just ID — no IP, email, or username
        if (event.user) {
          event.user = { id: event.user.id };
        }

        return event;
      },

      // Strip user metadata from transaction (performance) events as well
      beforeSendTransaction(event) {
        if (event.user) {
          event.user = { id: event.user.id };
        }
        return event;
      },
    });
  }

  // TIL: # is how you make a JS function private to the class!
  #getMetaContent(name) {
    return document.head.querySelector(`meta[name="${name}"]`)?.content;
  }
}
